import {useEffect, useState} from 'react';
import {useParams, Link, useHistory, useLocation} from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Divider,
  Row,
  Spin,
  Tag,
  Typography,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  CalendarOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import {Student} from '../../graphql/API';
import {StudentsService} from './StudentsService';
import {EnrollmentsService} from '../Enrollments/EnrollmentsService';
import type {NewEnrollment} from '@PikaDoApp/npm-pikado-logic/dist/modules/enrollment/graphql/enrollment.interface';

const studentsService = new StudentsService();
const enrollmentsService = new EnrollmentsService();

const enrollmentStatusColor: Record<string, string> = {
  ACTIVE: 'green',
  EXPIRED: 'red',
};

const placementStatusColor: Record<string, string> = {
  TODO: 'default',
  DOING: 'processing',
  DONE: 'success',
};

export const StudentDetails = () => {
  // 4fe2f51f-302e-40d1-ad9a-5d28d8e3fd69
  const [student, setStudent] = useState<Student | undefined>(undefined);
  const [enrollments, setEnrollments] = useState<NewEnrollment[]>([]);
  const [isEnrollmentsLoading, setIsEnrollmentsLoading] =
    useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  let params: {studentId: string} = useParams();
  const history = useHistory();
  const {Title, Text} = Typography;
  const location = useLocation();

  useEffect(() => {
    studentsService
      .getStudentById(params.studentId)
      .then(resp => {
        setIsLoading(false);
        setStudent(resp);
      })
      .catch(error => {
        setIsLoading(false);
        setIsError(true);
      });

    // Fetch enrollments separately
    setIsEnrollmentsLoading(true);
    enrollmentsService
      .getEnrollmentsByStudentId(params.studentId)
      .then(resp => {
        setEnrollments(resp);
        setIsEnrollmentsLoading(false);
      })
      .catch(error => {
        console.error('Error fetching student enrollments:', error);
        setIsEnrollmentsLoading(false);
      });
  }, [location.pathname]);

  console.log('enrollments', enrollments);
  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  //console.log('params.studentId', location.pathname);
  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Student </Title>
      <Button size={'large'} style={{float: 'right'}}>
        <Link
          // @ts-ignore
          to={{
            pathname: '/students/' + student?.id + '/edit',
            state: {student: student},
          }}>
          Edit
        </Link>
      </Button>
      {!isError && !isLoading && (
        <>
          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Student name
                </Text>
                <Text>{student?.name}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Parent name
                </Text>
                <Text>{student?.user?.parent?.name}</Text>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <div style={{margin: 20}}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                  }}>
                  <Text strong={true} style={{fontSize: 16}}>
                    Student Enrollments ({enrollments.length})
                  </Text>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() =>
                      history.push({
                        pathname: '/enrollments/create',
                        state: {
                          studentId: params.studentId,
                          studentName: student?.name,
                        },
                      })
                    }>
                    Create New Enrollment
                  </Button>
                </div>
                {isEnrollmentsLoading ? (
                  <Spin
                    size="small"
                    style={{display: 'block', textAlign: 'center', padding: 20}}
                  />
                ) : enrollments.length === 0 ? (
                  <Empty
                    description="No enrollments found"
                    style={{padding: 20}}
                  />
                ) : (
                  enrollments.map(enrollment => (
                    <Card
                      key={enrollment._id}
                      size="small"
                      style={{marginBottom: 8}}
                      hoverable
                      onClick={() =>
                        history.push('/enrollments/' + enrollment._id)
                      }>
                      <Row gutter={16} align="middle">
                        <Col flex="auto">
                          <Link
                            to={'/enrollments/' + enrollment._id}
                            style={{fontWeight: 600, fontSize: 14}}
                            onClick={e => e.stopPropagation()}>
                            {enrollment._id}
                          </Link>
                        </Col>
                        <Col>
                          <Tag
                            color={
                              enrollmentStatusColor[
                                enrollment.enrollmentStatus
                              ] || 'default'
                            }>
                            {enrollment.enrollmentStatus}
                          </Tag>
                        </Col>
                        <Col>
                          <Tag
                            color={
                              placementStatusColor[
                                enrollment.placementStatus
                              ] || 'default'
                            }>
                            Placement: {enrollment.placementStatus}
                          </Tag>
                        </Col>
                      </Row>
                      <Row gutter={16} style={{marginTop: 8}}>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <CalendarOutlined /> Start
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {formatDate(enrollment.startDate)}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <CalendarOutlined /> End
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {formatDate(enrollment.endDate)}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            <DollarOutlined /> Price
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {enrollment.finalPrice
                              ? `${enrollment.finalPrice.price ?? '—'} ${enrollment.finalPrice.currency}`
                              : '—'}
                          </Text>
                        </Col>
                        <Col span={6}>
                          <Text type="secondary" style={{fontSize: 12}}>
                            Payment
                          </Text>
                          <br />
                          <Text style={{fontSize: 13}}>
                            {enrollment.paymentMethod ?? '—'}
                            {enrollment.isFree && (
                              <Tag color="blue" style={{marginLeft: 4}}>
                                Free
                              </Tag>
                            )}
                          </Text>
                        </Col>
                      </Row>
                    </Card>
                  ))
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Gender
                </Text>
                <Text>{student?.gender}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Language
                </Text>
                <Link to={'/languages/' + student?.language?.id}>
                  {student?.language?.id}
                </Link>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Age
                </Text>
                <Text>{student?.age}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Joined at
                </Text>
                <Text>{student?.createdAt}</Text>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  User
                </Text>
                <Link to={'/users/' + student?.user?.id}>
                  {student?.user?.id}
                </Link>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Status
                </Text>
                <Text>{student?.status}</Text>
              </div>
            </Col>
          </Row>

          <Row>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Assigned to
                </Text>
                <Text>{student?.assignedTo}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Notes
                </Text>
                <Text>{student?.notes}</Text>
              </div>
            </Col>
          </Row>
        </>
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Student fetching error ⚠️</Title>}
    </div>
  );
};
